import * as Diff from 'diff';
import useLocale from 'next-translate/useTranslation';
import React from 'react';
import { Completion } from 'types';

import MarkdownBlock from './Chat/MarkdownBlock';
import { CaptionItem } from '../types/Caption';

type CaptionProps = {
  caption: CaptionItem;
  completion?: Completion;
};

const handleNewLines = (text: string) => {
  const items = text.split('\n');
  return items.map((item, key) => {
    return (
      <React.Fragment key={key}>
        {item}
        {key !== items.length - 1 && <br />}
      </React.Fragment>
    );
  });
};

const PublicCaption = ({ caption, completion }: CaptionProps) => {
  const { t } = useLocale('tools');
  const isNewsArticle =
    (completion?.preset || caption.preset) === 'news-article-generator';

  // Not every preset stores a `keywords` input (chat-style presets store none
  // at all), so fall back to the raw prompt and skip the block when empty.
  const inputText: string =
    (completion?.request_body as any)?.keywords ||
    completion?.user_prompt ||
    '';

  const generateDiff = (source: string, fixed: string) => {
    if (!fixed) {
      return source;
    }

    const diff = Diff.diffWords(fixed, source);

    const fixedTextDiff = diff.map((part: any, i: number) => {
      if (part.added) {
        return (
          <span key={i} className='bg-green-100 dark:bg-green-900'>
            {handleNewLines(part.value)}
          </span>
        );
      }
      if (part.removed) {
        return (
          <span key={i} className='bg-red-100 dark:bg-red-900'>
            {handleNewLines(part.value)}
          </span>
        );
      }

      return <span key={i}>{handleNewLines(part.value)}</span>;
    });

    return <>{fixedTextDiff}</>;
  };

  const shouldShowDiff =
    completion?.preset === 'grammar-corrector' ||
    caption?.preset === 'grammar-corrector' ||
    completion?.preset === 'paraphrasing-tool' ||
    caption?.preset === 'paraphrasing-tool' ||
    completion?.preset === 'native-speaker' ||
    caption?.preset === 'native-speaker';

  return (
    <li
      key={caption.id}
      className='messages relative bg-white px-4 py-6 shadow dark:bg-slate-800 sm:rounded-lg sm:p-6'
    >
      <article aria-labelledby={'caption-title-' + caption.id}>
        {inputText && (
          <div className='mb-7 mt-3'>
            <div>
              <b className='text-primary-700'>{t('INPUT')}</b>
            </div>

            <MarkdownBlock message={inputText} />
          </div>
        )}
        <div>
          <b className='text-primary-700'>{t('OUTPUT')}</b>
        </div>
        <div className='message text-md mt-2 space-y-4 text-gray-700 dark:text-slate-400'>
          {shouldShowDiff ? (
            <>
              {generateDiff(
                caption.text,
                caption?.prompt ||
                  (completion?.user_prompt as string) ||
                  (completion?.request_body as any).keywords,
              )}
            </>
          ) : (
            <MarkdownBlock
              message={caption.text.replace(/\n/g, '  \n').trim()}
              preferLinkText={isNewsArticle}
            />
          )}
        </div>
      </article>
    </li>
  );
};

export default PublicCaption;
